import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import {
  selectNavigationArtifact,
  type NavigationArtifact,
  type NavigationConfigKey,
} from '@/enterprise/data/navigationArtifactSelectors';
import apiClient from '@/lib/apiClient';
import useFeature from '@/lib/features';

type GetArtifactsResponseBody = paths['/artifacts']['get']['responses'][200]['content']['application/json'];
type GetArtifactsQuery = NonNullable<paths['/artifacts']['get']['parameters']['query']>;
type ArtifactsData = GetArtifactsResponseBody['data'];
type ArtifactType = NonNullable<GetArtifactsQuery['artifactTypes']>[number];

export type Artifact = ArtifactsData['artifacts'][number];

/** One `localizedContent` entry. Screens render this, not the API `content` field. */
export type LocalizedArtifactContent = NonNullable<Artifact['localizedContent']>[string];

/** Marketing message asset type */
export type MarketingMessageArtifactContent = Extract<LocalizedArtifactContent, { title: string }>;

/** Alert asset type. It has a body and no title, so a marketing message is not an alert. */
export type AlertArtifactContent = Exclude<Extract<LocalizedArtifactContent, { body: string }>, { title: string }>;

/** Artifact that has a locale entry. `content` is that entry. */
export type ResolvedArtifact = Omit<Artifact, 'content'> & {
  content: LocalizedArtifactContent;
};

/** Artifact passed to winner selection. `content` is the locale entry it will render. */
export type ArtifactWithDisplayContent = Omit<Artifact, 'content'> & {
  content?: LocalizedArtifactContent | null;
};

export type RenderableAlertArtifact = ResolvedArtifact & {
  content: AlertArtifactContent;
};

export type RenderableMarketingArtifact = ResolvedArtifact & {
  content: MarketingMessageArtifactContent;
};

const ARTIFACT_CACHE_MS = 1000 * 60 * 5;

/** Marketing and alert types, fetched together. Navigation is separate. */
export const CONTENT_ARTIFACT_TYPES = ['MARKETING_MESSAGE', 'ALERT'] as const satisfies readonly ArtifactType[];

/**
 * Placements on that shared fetch. Add one when a new slot ships.
 *
 * `useMarketingArtifacts` and `useAlertArtifacts` take an array rather than a single placement
 * because both surfaces are expected to select across more than one of these.
 */
export const CONTENT_ARTIFACT_PLACEMENTS = ['early-pay.home.side-card', 'early-pay.global.operational'] as const;

/** Enterprise homepage side card. */
export const HOMEPAGE_SIDECARD_PLACEMENT: (typeof CONTENT_ARTIFACT_PLACEMENTS)[number] = 'early-pay.home.side-card';

/** App-wide operational alert. */
export const OPERATIONAL_ALERT_PLACEMENT: (typeof CONTENT_ARTIFACT_PLACEMENTS)[number] = 'early-pay.global.operational';

export const CONTENT_ARTIFACTS_QUERY_KEY = ['artifacts', 'content'] as const;
export const NAVIGATION_ARTIFACTS_QUERY_KEY = ['artifacts', 'navigation'] as const;

const matchesPlacements = (artifact: Artifact, placements: string[]): boolean =>
  artifact.placements?.some((placement) => placements.includes(placement)) ?? false;

/** Highest priority wins. A tie keeps the first match. */
export const selectHighestPriorityArtifact = <T extends ArtifactWithDisplayContent>(
  artifacts: ArtifactWithDisplayContent[],
  isRenderable: (artifact: ArtifactWithDisplayContent) => artifact is T
): T | undefined =>
  artifacts.filter(isRenderable).reduce<T | undefined>((best, current) => {
    if (!best || current.priority > best.priority) {
      return current;
    }

    return best;
  }, undefined);

/** True when the entry is alert copy. */
export const isAlertContent = (content: LocalizedArtifactContent | null | undefined): content is AlertArtifactContent =>
  content != null && 'body' in content && !('title' in content) && !('primaryCta' in content) && !('text' in content);

const isRenderableAlertArtifact = (
  artifact: ArtifactWithDisplayContent,
  placements: string[]
): artifact is RenderableAlertArtifact =>
  artifact.artifactType === 'ALERT' &&
  matchesPlacements(artifact, placements) &&
  isAlertContent(artifact.content) &&
  artifact.content.body.trim().length > 0;

/** Highest-priority alert for these placements. */
export const selectHighestPriorityAlertArtifact = (
  artifacts: ArtifactWithDisplayContent[],
  placements: string[]
): RenderableAlertArtifact | undefined =>
  selectHighestPriorityArtifact(artifacts, (artifact): artifact is RenderableAlertArtifact =>
    isRenderableAlertArtifact(artifact, placements)
  );

export const isMarketingMessageContent = (
  content: LocalizedArtifactContent | null | undefined
): content is MarketingMessageArtifactContent => content != null && 'title' in content;

/** Fallback locale. App English is `en`; the map key is `en-US`. */
const DEFAULT_ARTIFACT_LOCALE = 'en-US';

/** App language code to `localizedContent` key. */
const ARTIFACT_LOCALE_BY_INPUT: Record<string, string> = {
  en: DEFAULT_ARTIFACT_LOCALE,
  'en-us': DEFAULT_ARTIFACT_LOCALE,
  fr: 'fr',
  'zh-cn': 'zh-CN',
  'zh-tw': 'zh-TW',
  ja: 'ja',
  de: 'de',
  es: 'es',
  nl: 'nl',
  it: 'it',
  ru: 'ru',
  tr: 'tr',
  'pt-br': 'pt-BR',
};

const canonicalArtifactLocale = (language: string): string => {
  const trimmed = language.trim();

  return ARTIFACT_LOCALE_BY_INPUT[trimmed.toLowerCase()] ?? trimmed;
};

const findLocalizedContent = (
  localizedContent: NonNullable<Artifact['localizedContent']>,
  locale: string
): LocalizedArtifactContent | undefined => {
  const matchKey = Object.keys(localizedContent).find((key) => key.toLowerCase() === locale.toLowerCase());

  return matchKey ? localizedContent[matchKey] : undefined;
};

/**
 * Copy for a marketing message, alert, or nav item.
 * Tries the current language, then `en-US`. Returns nothing when neither entry exists.
 * Runs during render, so changing language does not refetch.
 */
export const resolveLocalizedArtifact = (artifact: Artifact, language: string): ResolvedArtifact | undefined => {
  const { localizedContent } = artifact;

  if (
    !localizedContent ||
    (artifact.artifactType !== 'MARKETING_MESSAGE' &&
      artifact.artifactType !== 'ALERT' &&
      artifact.artifactType !== 'NAVIGATION')
  ) {
    return undefined;
  }

  const requestedLocale = canonicalArtifactLocale(language);
  const locales =
    requestedLocale.toLowerCase() === DEFAULT_ARTIFACT_LOCALE.toLowerCase()
      ? [DEFAULT_ARTIFACT_LOCALE]
      : [requestedLocale, DEFAULT_ARTIFACT_LOCALE];

  for (const locale of locales) {
    const entry = findLocalizedContent(localizedContent, locale);

    if (entry) {
      return { ...artifact, content: entry };
    }
  }

  return undefined;
};

/** Locale entries for the current language. Artifacts with no usable entry are left out. */
export const resolveLocalizedArtifacts = (artifacts: Artifact[], language: string): ResolvedArtifact[] =>
  artifacts.flatMap((artifact) => {
    const resolved = resolveLocalizedArtifact(artifact, language);

    return resolved ? [resolved] : [];
  });

const isRenderableMarketingArtifact = (
  artifact: ArtifactWithDisplayContent,
  placements: string[]
): artifact is RenderableMarketingArtifact => {
  const { content } = artifact;

  return (
    artifact.artifactType === 'MARKETING_MESSAGE' &&
    matchesPlacements(artifact, placements) &&
    isMarketingMessageContent(content) &&
    content.title.length > 0 &&
    content.body.length > 0
  );
};

/** Highest-priority marketing message for these placements. */
export const selectHighestPriorityMarketingArtifact = (
  artifacts: ArtifactWithDisplayContent[],
  placements: string[]
): RenderableMarketingArtifact | undefined =>
  selectHighestPriorityArtifact(artifacts, (artifact): artifact is RenderableMarketingArtifact =>
    isRenderableMarketingArtifact(artifact, placements)
  );

/** Homepage side card shows at most this many marketing artifacts. */
export const HOMEPAGE_MARKETING_ARTIFACT_LIMIT = 3;

/**
 * Renderable MARKETING_MESSAGE artifacts for the caller placements, highest priority first.
 * Ties keep response order. The result is capped at `limit`.
 */
export const selectTopMarketingArtifacts = (
  artifacts: ArtifactWithDisplayContent[],
  placements: string[],
  limit: number = HOMEPAGE_MARKETING_ARTIFACT_LIMIT
): RenderableMarketingArtifact[] =>
  artifacts
    .map((artifact, index) => ({ artifact, index }))
    .filter((entry): entry is { artifact: RenderableMarketingArtifact; index: number } =>
      isRenderableMarketingArtifact(entry.artifact, placements)
    )
    .sort((left, right) => {
      if (right.artifact.priority !== left.artifact.priority) {
        return right.artifact.priority - left.artifact.priority;
      }

      return left.index - right.index;
    })
    .slice(0, limit)
    .map((entry) => entry.artifact);

interface FetchArtifactsParams {
  artifactTypes: ArtifactType[];
  placements?: string[];
  limit?: number;
}

const fetchArtifacts = async ({ artifactTypes, placements, limit }: FetchArtifactsParams): Promise<Artifact[]> => {
  const response = await apiClient
    .get('api/supplier-experience/artifacts', {
      searchParams: qs.stringify(
        {
          artifactTypes,
          ...(placements ? { placements } : {}),
          ...(limit !== undefined ? { limit } : {}),
        } satisfies GetArtifactsQuery,
        { arrayFormat: 'brackets', indices: false }
      ),
    })
    .json<GetArtifactsResponseBody>();

  return response.data.artifacts.filter((artifact) => artifact.localizedContent != null);
};

type ArtifactsQueryKey = typeof CONTENT_ARTIFACTS_QUERY_KEY | typeof NAVIGATION_ARTIFACTS_QUERY_KEY;

interface ArtifactsQueryOptions {
  /** Overrides the default of 3 retries. */
  retry?: boolean | number;
  placements?: string[];
  limit?: number;
}

/**
 * One artifacts request. Runs only when `enterprise-ui_useMarketingArtifacts` is on.
 * Do not send `accountId`.
 */
const useArtifactsQuery = <TData = Artifact[]>(
  queryKey: ArtifactsQueryKey,
  artifactTypes: ArtifactType[],
  select?: (data: Artifact[]) => TData,
  options?: ArtifactsQueryOptions
) => {
  const [useArtifactsEnabled] = useFeature('enterprise-ui_useMarketingArtifacts');

  return useQuery({
    queryKey,
    queryFn: () =>
      fetchArtifacts({
        artifactTypes,
        placements: options?.placements,
        limit: options?.limit,
      }),
    enabled: useArtifactsEnabled,
    throwOnError: false,
    /**
     * An artifacts outage must not block the app. `ArtifactsGate` renders a full-page loader while
     * these queries load, which unmounts their consumers; on the default `retryOnMount: true`, each
     * consumer remount refetches the failed query, and a refetch with no cached data resets its
     * status to `pending` — so the gate shows the loader again and nothing ever renders. Keeping the
     * failure means `isError` sticks, consumers render their empty state, and the app paints
     * (SUPP-19611). Recovery comes from reconnect, an explicit invalidation, or a reload.
     */
    retryOnMount: false,
    select,
    staleTime: ARTIFACT_CACHE_MS,
    gcTime: ARTIFACT_CACHE_MS,
    ...(options?.retry !== undefined ? { retry: options.retry } : {}),
  });
};

/** Shared marketing and alert request. The gate starts it so other screens read the cache. */
const useContentArtifactsQuery = <TData = Artifact[]>(select?: (data: Artifact[]) => TData) =>
  useArtifactsQuery(CONTENT_ARTIFACTS_QUERY_KEY, [...CONTENT_ARTIFACT_TYPES], select, {
    placements: [...CONTENT_ARTIFACT_PLACEMENTS],
    limit: HOMEPAGE_MARKETING_ARTIFACT_LIMIT,
    retry: 1,
  });

/** True while marketing and alerts are still loading, so the first paint can wait for them. */
export const useContentArtifactsGate = (): { isLoading: boolean } => {
  const { isLoading } = useContentArtifactsQuery();

  return { isLoading };
};

/**
 * Refetches marketing and alerts after an offer or draft submit. Leaves navigation cached.
 * Callers do not wait on it.
 */
export const invalidateContentArtifacts = (queryClient: QueryClient): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: CONTENT_ARTIFACTS_QUERY_KEY });

/**
 * The shared navigation request, resolved for the current language. Not cleared when an offer is
 * submitted. Private: screens read one product through `useNavigationArtifact`, and the first paint
 * waits on `useNavigationArtifactsGate`.
 *
 * Resolution copies the chosen locale entry onto a new artifact object, so it is memoized. The
 * result reaches `useMemo` deps in `useNavigationItems` — a fresh array on every render rebuilds the
 * nav list, and the route-sync effect in `Navigation` then re-triggers itself.
 */
const useNavigationArtifactsQuery = () => {
  const { i18n } = useTranslation();
  const query = useArtifactsQuery(NAVIGATION_ARTIFACTS_QUERY_KEY, ['NAVIGATION'], undefined, { retry: 1 });
  const { data } = query;
  const { language } = i18n;

  const resolved = useMemo(
    () => (data === undefined ? undefined : resolveLocalizedArtifacts(data, language)),
    [data, language]
  );

  return {
    ...query,
    data: resolved,
  };
};

/** True while navigation artifacts are still loading, so the first paint can wait for them. */
export const useNavigationArtifactsGate = (): { isLoading: boolean } => {
  const { isLoading } = useNavigationArtifactsQuery();

  return { isLoading };
};

/**
 * The nav artifact qualifying this user for one financing product. `undefined` means the user does
 * not qualify; read `isPending` for load state.
 *
 * Takes a config key rather than a selector so the memo key is a string. A selector argument would
 * be a new reference on every render unless every call site remembered to hoist it, which is how the
 * side nav came to re-render without end (SUPP-19734).
 */
export const useNavigationArtifact = (
  configKey: NavigationConfigKey
): {
  data: NavigationArtifact | undefined;
  isPending: boolean;
  isError: boolean;
  isLoading: boolean;
  error: Error | null;
} => {
  const query = useNavigationArtifactsQuery();
  const { data: artifacts } = query;

  const artifact = useMemo(
    () => (artifacts === undefined ? undefined : selectNavigationArtifact(configKey, artifacts)),
    [configKey, artifacts]
  );

  return {
    ...query,
    data: artifact,
  };
};

/** Resolves each artifact, then takes the top marketing messages. */
const selectLocalizedMarketingArtifacts = (
  artifacts: Artifact[] | undefined,
  language: string,
  placements: string[]
): RenderableMarketingArtifact[] | undefined => {
  if (!artifacts) {
    return undefined;
  }

  return selectTopMarketingArtifacts(resolveLocalizedArtifacts(artifacts, language), placements);
};

/**
 * Up to `HOMEPAGE_MARKETING_ARTIFACT_LIMIT` marketing messages for these placements, in the
 * current language, highest priority first.
 *
 * Memoized for the reason given on `useNavigationArtifacts`. Pass `placements` as a module-level
 * constant so the memo holds across renders.
 */
export const useMarketingArtifacts = (placements: string[]) => {
  const { i18n } = useTranslation();
  const query = useContentArtifactsQuery();
  const { data } = query;
  const { language } = i18n;

  const selected = useMemo(
    () => selectLocalizedMarketingArtifacts(data, language, placements),
    [data, language, placements]
  );

  return {
    ...query,
    data: selected,
  };
};

/** Resolves each artifact, then picks the alert winner. */
const selectLocalizedAlertArtifact = (
  artifacts: Artifact[] | undefined,
  language: string,
  placements: string[]
): RenderableAlertArtifact | undefined => {
  if (!artifacts) {
    return undefined;
  }

  return selectHighestPriorityAlertArtifact(resolveLocalizedArtifacts(artifacts, language), placements);
};

/**
 * Highest-priority alert for these placements, in the current language. The shared fetch asks SEA
 * for up to `HOMEPAGE_MARKETING_ARTIFACT_LIMIT` artifacts per placement; this still returns one.
 *
 * Memoized for the reason given on `useNavigationArtifacts`. Pass `placements` as a module-level
 * constant so the memo holds across renders.
 */
export const useAlertArtifacts = (placements: string[]) => {
  const { i18n } = useTranslation();
  const query = useContentArtifactsQuery();
  const { data } = query;
  const { language } = i18n;

  const selected = useMemo(
    () => selectLocalizedAlertArtifact(data, language, placements),
    [data, language, placements]
  );

  return {
    ...query,
    data: selected,
  };
};
